import React, {Fragment, useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {updateExamRequest} from "../../slices/examSlice";
import CustomRichTextEditor from "../form/input/CustomRichTextEditor";
import Paper from "../ui/customs/Paper";
import {QuestionTypes} from "../../types/Question";
import MyEditor5 from "../form/input/MyEditor5";
import {skillTypes} from "../../types/constants";
import Select from "../form/Select";
import Checkbox from "../form/input/Checkbox";
import {apiRequestWithAuth} from "../../utils/api";
import SelectV2 from "../form/SelectV2";

type HeaderProps = {
    exam: any
};

type Category = {
    value: string;
    label: string;
};

const Header: React.FC<HeaderProps> = ({exam}) => {
    const dispatch = useDispatch();

    const [title, setTitle] = useState(exam.title || "");
    const [description, setDescription] = useState(exam.shortDescription || "");
    const [categories, setCategories] = useState<Category[]>([]);
    const hasFetchedCategories = useRef(false);

    const handleUpdate = (field: "title" | "timeLimit" | "shortDescription" | "categoryId" | "skillId" | "isMockTest", value: any) => {
        if (typeof value === "string" && !value.trim()) return;
        dispatch(updateExamRequest({examId: exam.id, [field]: value}));
    };
    const [categoryId, setCategoryId] = useState('');
    const [skillId, setSkillId] = useState('');
    const [isMockTest, setIsMockTest] = useState(false);
    const [timeLimit, setTimeLimit] = useState(exam.timeLimit || 0);

    const fetchCategories = async () => {
        if (hasFetchedCategories.current) return;

        hasFetchedCategories.current = true;
        try {
            const res = await apiRequestWithAuth("/category", "GET", {});
            const rawCategories = res.result;

            // B1: gom nhóm theo parentId
            const mapByParent: Record<string, any[]> = {};
            rawCategories.forEach((cat: any) => {
                const parentId = cat.parentId ?? "root";
                if (!mapByParent[parentId]) mapByParent[parentId] = [];
                mapByParent[parentId].push(cat);
            });

            // B2: hàm đệ quy để build tree phẳng + prefix
            const buildHierarchy = (parentId = "root", prefix = ""): any[] => {
                const children = mapByParent[parentId] || [];
                return children.flatMap((child) => [
                    {
                        value: child.id.toString(),
                        label: prefix + child.title,
                    },
                    ...buildHierarchy(child.id.toString(), prefix + "-- "),
                ]);
            };

            // B3: set categories
            setCategories(buildHierarchy());
        } catch (error) {
            console.error("Lỗi lấy danh mục:", error);
        }
    };

    useEffect(() => {
        setTitle(exam.title);
        setDescription(exam.shortDescription);
        setCategoryId(exam.categoryId);
        setSkillId(exam.skillId);
        setIsMockTest(exam.isMockTest);
        setTimeLimit(exam.timeLimit);
    }, [exam]);

    useEffect(() => {
        fetchCategories();
    }, []);

    if (!exam) return <p>Loading...</p>;

    return (
        <Fragment>
            <div className="mb-6 grow-1 bg-white rounded shadow">
                <Paper
                    elevation={2}
                    style={{
                        padding: 24,
                        borderTop: "8px solid #9C27B0",
                        backgroundColor: "#fafafa",
                    }}
                >
                    <input
                        defaultValue={title}
                        onBlur={() => handleUpdate("title", title)}
                        className="w-full border border-gray-300 rounded-lg p-3 text-sm shadow-sm focus:ring focus:ring-indigo-200 focus:outline-none mb-4"
                        onChange={(e) => setTitle(e.target.value)}
                    />
                    <div className="grid grid-cols-3 gap-4 w-full mb-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Chọn danh mục</label>
                            <SelectV2
                                options={categories}
                                placeholder="Chọn danh mục"
                                defaultValue={categoryId}
                                onChange={(val) => {
                                    setCategoryId(val)
                                    handleUpdate("categoryId", val);
                                }}
                                className="dark:bg-dark-900"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Loại</label>
                            <SelectV2
                                options={skillTypes}
                                placeholder="Chọn danh mục"
                                defaultValue={skillId}
                                onChange={(val) => {
                                    setSkillId(val)
                                    handleUpdate("skillId", val);
                                }}
                                className="border px-3 py-2 rounded"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Thời gian làm bài (phút)</label>
                            <input
                                type="number"
                                min="1"
                                max="300"
                                value={timeLimit}
                                onChange={(e) => setTimeLimit(parseInt(e.target.value) || 0)}
                                onBlur={() => handleUpdate("timeLimit", timeLimit)}
                                className="w-full border border-gray-300 rounded-lg p-2 text-sm shadow-sm focus:ring focus:ring-indigo-200 focus:outline-none"
                                placeholder="0"
                            />
                        </div>
                        <div className="flex flex-col gap-3 pt-5">
                            <label className="inline-flex items-center">
                                <Checkbox
                                    checked={isMockTest}
                                    onChange={(val) => {
                                        setIsMockTest(val)
                                        handleUpdate("isMockTest", val ? 1 : 0);
                                    }}
                                    label="Thi thử"
                                />
                            </label>
                        </div>
                    </div>

                    <MyEditor5
                        content={description}
                        onBlur={(value) => handleUpdate("shortDescription", value)}
                        rows={4}
                    />
                </Paper>
            </div>
        </Fragment>
    );
};

export default Header;
